import { createApi } from "@reduxjs/toolkit/query/react"
import { getUpdatedStates } from "utils/localCacheStateUtils"
import { CREATE_ITEM, DELETE_ITEM, LIKE_ITEM } from "constants/index"
import { RootState } from "appRedux/store"
import { baseQueryWithMiddlewares } from "appRedux/baseQuery"
import { userProfileApi } from "appRedux/slices/userApiSlice"
import { wishListApi } from "appRedux/slices/wishListApiSlice"
import { updateUserProfile, updateUserWishItems, updateUserWishLists, updateUserWishListsWithFirstFourItems } from "appRedux/slices/userSlice"
import { updateCurrentItemIndex, updateCurrentItemInfo, updateWishListInfo } from "appRedux/slices/wishListSlice"
import {
  handleOnGetLikesQueryStarted,
  handleOnGetCommentsQueryStarted,
  handleOnGetWishItemGeniesQueryStarted,
  handleOnRollbackTrueWishQueryStarted,
  handleOnMakeWishComeTrueQueryStarted,
  handleOnAddCommentQueryStarted,
  handleOnEditWishItemQueryStarted,
  handleOnGetWishItemsQueryStarted,
  handleOnGetPopularItemsQueryStarted
} from "appRedux/helpers/wishItemApiHelpers"

export const wishItemApi = createApi({
  reducerPath: "wishItemApi",
  baseQuery: baseQueryWithMiddlewares("/wishlist/item"),
  tagTypes: ["Genies"],
  endpoints: (builder) => ({
    getWishItems: builder.query<GetWishItemsResponseDto, void>({
      query: () => "",
      keepUnusedDataFor: 5,
      onQueryStarted: handleOnGetWishItemsQueryStarted
    }),
    getWishItem: builder.query<SingleWishItem, number>({
      query: (id) => `/${id}`,
      keepUnusedDataFor: 5,
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateCurrentItemInfo(data))
        } catch (error) {
          console.error(`Error getting item with id: ${id}: `, error)
        }
      }
    }),
    createWishItem: builder.mutation({
      query: (body) => ({
        url: "",
        method: "PUT",
        body: body,
        formData: true
      }),
      transformResponse: (response: number) => ({ id: response }),
      onQueryStarted: async (formDataBody, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
          const wishItems = state.user.wishItems || []
          const list = state?.wishList?.listInfo
          let objectBody = Object.fromEntries(formDataBody.entries()) as WishItemRequestPayload
          if (!userProfile) return
          const { listInfo, userProfileLists, userWishLists, itemInfo, userWishItems } = getUpdatedStates(
            CREATE_ITEM,
            wishlistsWithFirstFourItems,
            list,
            { ...objectBody, id: data.id },
            wishItems
          )

          dispatch(updateUserProfile({
            ...userProfile,
            itemsCount: userProfile.itemsCount + 1
          }))
          dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
          dispatch(updateUserWishLists({ wishlists: userWishLists }))
          dispatch(updateCurrentItemInfo(itemInfo))
          dispatch(updateUserWishItems({ items: userWishItems }))
          dispatch(userProfileApi.util.updateQueryData("getUserProfile", undefined, (prevData) => {
            prevData.itemsCount = userWishItems?.length || 0
          }))

          if (state.user.profile?.handle) {
            dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
              prevData.wishlists = userProfileLists
            }))
          }
          dispatch(wishListApi.util.updateQueryData("getWishLists", undefined, (prevData) => {
            prevData.wishlists = userWishLists
          }))
          dispatch(wishItemApi.util.updateQueryData("getWishItems", undefined, (prevData) => {
            if (!prevData.items) return
            prevData.items.push(itemInfo)
          }))
          if (list?.id) {
            dispatch(updateWishListInfo(listInfo))
            if (listInfo) {
              dispatch(wishListApi.util.updateQueryData("getWishList", listInfo.id, () => {
                return listInfo
              }))
            }
            const index = listInfo?.listItems?.length || 0
            dispatch(updateCurrentItemIndex(index))
          } else {
            dispatch(updateCurrentItemIndex(0))
          }
        } catch (error) {
          console.error("Error adding item:", error)
        }
      }
    }),
    editWishItem: builder.mutation({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: body,
        formData: true
      }),
      onQueryStarted: handleOnEditWishItemQueryStarted
    }),
    deleteWishItem: builder.mutation<any, number>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
          const list = state?.wishList?.listInfo
          const item = state?.wishList?.itemInfo
          const wishItems = state.user.wishItems || []
          const currentItemIndex = state?.wishList?.currentItemIndex
          if (!item || !userProfile) return
          const { listInfo, userProfileLists, userWishLists, userWishItems } = getUpdatedStates(
            DELETE_ITEM,
            wishlistsWithFirstFourItems,
            list,
            item,
            wishItems
          )
          dispatch(updateUserProfile({
            ...userProfile,
            itemsCount: userProfile.itemsCount - 1
          }))
          dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
          dispatch(updateUserWishLists({ wishlists: userWishLists }))
          dispatch(updateUserWishItems({ items: userWishItems }))

          dispatch(userProfileApi.util.updateQueryData("getUserProfile", undefined, (prevData) => {
            prevData.itemsCount = userProfile.itemsCount - 1
          }))
          if (state.user.profile?.handle) {
            dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
              prevData.wishlists = userProfileLists
            }))
          }
          dispatch(wishListApi.util.updateQueryData("getWishLists", undefined, (prevData) => {
            prevData.wishlists = userWishLists
          }))
          dispatch(wishItemApi.util.updateQueryData("getWishItems", undefined, (prevData) => {
            if (!prevData.items) return
            return { items: userWishItems }
          }))
          if (list?.id !== undefined) {
            dispatch(updateWishListInfo(listInfo))
            const index = currentItemIndex > 0 ? currentItemIndex - 1 : 0
            dispatch(updateCurrentItemIndex(index))
            if (listInfo) {
              dispatch(wishListApi.util.updateQueryData("getWishList", Number(list.id), (prevData) => {
                prevData.listItems = listInfo.listItems || []
                prevData.itemCount = listInfo.itemCount || 0
              }))
            }
          }
        } catch (error) {
          console.error("Error deleting item:", error)
        }
      }
    }),
    likeWishItem: builder.query({
      query: (id) => `/${id}/like`,
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
          const list = state?.wishList?.listInfo
          const item = state?.wishList?.itemInfo
          const wishItems = state.user.wishItems || []
          if (!item || !userProfile) return
          const { listInfo, userProfileLists, userWishLists, itemInfo, userWishItems } = getUpdatedStates(
            LIKE_ITEM,
            wishlistsWithFirstFourItems,
            list,
            item,
            wishItems
          )

          dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
          dispatch(updateUserWishLists({ wishlists: userWishLists }))
          dispatch(updateCurrentItemInfo(itemInfo))
          dispatch(updateUserWishItems({ items: userWishItems }))

          if (state.user.profile?.handle) {
            dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
              prevData.wishlists = userProfileLists
            }))
          }
          dispatch(wishListApi.util.updateQueryData("getWishLists", undefined, (prevData) => {
            prevData.wishlists = userWishLists
          }))
          dispatch(wishItemApi.util.updateQueryData("getWishItems", undefined, (prevData) => {
            if (!prevData.items) return
            prevData.items = userWishItems
          }))
          dispatch(wishItemApi.util.updateQueryData("getWishItem", id, (prevData) => {
            prevData.isLiked = itemInfo?.isLiked
            prevData.likeCount = itemInfo?.likeCount
          }))
          if (listInfo) {
            dispatch(updateWishListInfo(listInfo))
          }
        } catch (error) {
          console.error(`Error like/dislike wish item with id: ${id}: `, error)
        }
      }
    }),
    getComments: builder.query<GetWishItemCommentsResponseDto, number>({
      query: (id) => `/${id}/comment`,
      keepUnusedDataFor: 5,
      onQueryStarted: handleOnGetCommentsQueryStarted
    }),
    addComment: builder.mutation({
      query: ({ id, body }) => ({
        url: `/${id}/comment`,
        method: "PUT",
        body: body
      }),
      onQueryStarted: handleOnAddCommentQueryStarted
    }),
    getLikes: builder.query<GetWishItemLikesResponseDto, number>({
      query: (id) => `/${id}/likes`,
      keepUnusedDataFor: 5,
      onQueryStarted: handleOnGetLikesQueryStarted
    }),
    makeWishComeTrue: builder.mutation<void, { id: number, genieIds: number[] }>({
      query: ({ id, genieIds }) => ({
        url: `/${id}/come-true`,
        method: "POST",
        body: { genieIds }
      }),
      onQueryStarted: handleOnMakeWishComeTrueQueryStarted
    }),
    rollbackTrueWish: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/true-wish`,
        method: "DELETE"
      }),
      invalidatesTags: ["Genies"],
      onQueryStarted: handleOnRollbackTrueWishQueryStarted
    }),
    getGenies: builder.query<GetGeniesResponseDto, number>({
      query: (id) => `/${id}/genies`,
      providesTags: ["Genies"],
      onQueryStarted: handleOnGetWishItemGeniesQueryStarted
    }),
    getPopularItems: builder.query<GetGeniesResponseDto, void>({
      query: () => "/popular",
      keepUnusedDataFor: 3,
      onQueryStarted: handleOnGetPopularItemsQueryStarted
    })
  })
})

export const {
  useLazyGetWishItemQuery,
  useLazyGetWishItemsQuery,
  useCreateWishItemMutation,
  useEditWishItemMutation,
  useDeleteWishItemMutation,
  useLazyLikeWishItemQuery,
  useAddCommentMutation,
  useLazyGetCommentsQuery,
  useLazyGetLikesQuery,
  useMakeWishComeTrueMutation,
  useRollbackTrueWishMutation,
  useLazyGetGeniesQuery,
  useGetPopularItemsQuery
} = wishItemApi
